import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button';
import EntryView from './EntryView';

const Content = styled(DialogContent)`
  padding-top: 0;
  padding-bottom: 0;
`;

const Actions = styled(DialogActions)`
  justify-content: flex-start;
`;

const Spacer = styled('div')`
  flex: 1 1 auto;
`;

const CollectionEntryViewDialog = ({ open, onClose, entry, attributes, onPrev, onNext, hasPrev, hasNext }) => {
  const { t } = useTranslation('collections');

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'ArrowLeft' && hasPrev) {
        onPrev();
      } else if (event.key === 'ArrowRight' && hasNext) {
        onNext();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, hasPrev, hasNext, onPrev, onNext]);

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <Content>
        <EntryView entry={entry} attributes={attributes} />
      </Content>
      <Actions>
        <IconButton onClick={onPrev} disabled={!hasPrev} aria-label={t('preview.previous')}>
          <ChevronLeftRoundedIcon />
        </IconButton>
        <IconButton onClick={onNext} disabled={!hasNext} aria-label={t('preview.next')}>
          <ChevronRightRoundedIcon />
        </IconButton>
        <Spacer />
        <Button variant="text" onClick={onClose}>{t('preview.close')}</Button>
      </Actions>
    </Dialog>
  );
};

CollectionEntryViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  entry: PropTypes.object,
  attributes: PropTypes.array,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  hasPrev: PropTypes.bool,
  hasNext: PropTypes.bool,
};

export default CollectionEntryViewDialog;
